import { makeAutoObservable, runInAction } from 'mobx'
import { uiStore } from 'shared/store/uiStore'
import { type IEntitiesStore } from 'entities/EntitiesExport'
import { FeatureRestrictedModalStore } from 'entities/UsersPermissions'
import { CallModalStore } from 'widgets/CallModal'
import { ContactCreateModalStore } from 'widgets/ContactCreateModal'
import { SearchInputStore } from 'pages/conversations/pages/search/ui/SearchInput'
import { RecentSearchesStore } from 'pages/conversations/pages/search/ui/RecentSearches'
import { ResultsStore } from 'pages/conversations/pages/search/ui/Results'
import { SearchPageStateEnum } from 'pages/conversations/pages/search/store/types'

export class SearchPageStore {
  private _loading = true
  readonly searchInputStore: SearchInputStore
  readonly recentSearchesStore: RecentSearchesStore
  readonly resultsStore: ResultsStore
  readonly callModalStore: CallModalStore
  readonly contactCreateModalStore: ContactCreateModalStore
  private _visibleUI = false

  constructor(private _entities: IEntitiesStore) {
    this.recentSearchesStore = new RecentSearchesStore()
    this.searchInputStore = new SearchInputStore(this._entities)
    this.resultsStore = new ResultsStore(this._entities)
    this.callModalStore = new CallModalStore(this._entities)
    this.contactCreateModalStore = new ContactCreateModalStore(this.callModalStore, this._entities)

    this.searchInputStore.setConfig({
      onBlur: (value) => {
        this.recentSearchesStore.createItem(value)
      },
      onSearch: (value) => {
        const restored = this.resultsStore.tryRestoreFromSessionCache(value)

        if (!restored) {
          this.resultsStore.loadAll(value)
        }
        this._setSearchQuery(value)
      },
    })
    this.recentSearchesStore.setConfig({
      onSearch: (value) => {
        this.searchInputStore.setSearch(value)
      },
    })
    this.contactCreateModalStore.setConfig({
      onActionSubmit: (contact) => {
        this.resultsStore.handleGoToProfileNewConversation(contact)
      },
    })

    makeAutoObservable(this)
  }

  private _setSearchQuery = (value: string) => {
    if (this._visibleUI) {
      const params = new URLSearchParams(window.location.search)

      if (value) {
        params.set('search', value)
      } else {
        params.delete('search')
      }

      uiStore.changeQueryRoute(params.toString())
    }
  }

  setVisibleUI = (value: boolean) => {
    this._visibleUI = value
  }

  load = async () => {
    runInAction(() => {
      this._loading = true
    })

    this.resultsStore.init()
    this.searchInputStore.initSearch()

    await Promise.all([
      this._entities.integrationsStore.initPromise,
      this._entities.inboxesStore.initPromise,
      this._entities.userPermissionsStore.initPromise,
      this.recentSearchesStore.loadItems(),
    ])

    runInAction(() => {
      this._loading = false
    })
  }

  dispose = () => {
    this.recentSearchesStore.dispose()
    this.searchInputStore.dispose()
    this.resultsStore.dispose()
    this.contactCreateModalStore.reset()
  }

  onOpenCreateContactModal = () => {
    if (this._entities.userPermissionsStore.getItem('can_create_contacts')) {
      this.contactCreateModalStore.setNumber(this.searchInputStore.search)
      this.contactCreateModalStore.openModal()
    } else {
      new FeatureRestrictedModalStore('can_create_contacts')
    }
  }

  get state(): SearchPageStateEnum {
    if (this.searchInputStore.search.length >= 3) {
      if (this.resultsStore.loading) {
        return SearchPageStateEnum.loading
      }

      if (!this.resultsStore.count) {
        return SearchPageStateEnum.create_contact
      }

      return SearchPageStateEnum.results
    }

    if (this.recentSearchesStore.loading || this._loading) {
      return SearchPageStateEnum.loading
    }

    if (this.recentSearchesStore.count) {
      return SearchPageStateEnum.recent_searches
    }

    return SearchPageStateEnum.empty_state
  }
}
